import { ManifestUrl } from "./install-contract";

/** Same-origin projection of the release workflow's R2 `latest.json` pointer. */
export const LatestLocalAgentReleasePath = "/api/local-agent/latest-release";

export type LatestLocalAgentRelease = {
  version: string;
};

type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export function parseLatestLocalAgentRelease(value: unknown): LatestLocalAgentRelease {
  if (!value || typeof value !== "object" || !("version" in value)) {
    throw new Error("The latest Salesko Local Agent release did not include a version.");
  }

  const version = (value as { version?: unknown }).version;
  if (typeof version !== "string" || version.trim().length === 0 || version.length > 160) {
    throw new Error("The latest Salesko Local Agent release version is invalid.");
  }

  return { version: version.trim() };
}

/**
 * The browser reads the R2 release pointer through this Worker-owned endpoint,
 * so the public downloads bucket does not need a separate browser CORS policy.
 */
export async function latestLocalAgentReleaseResponse(
  request: Request,
  fetchManifest: Fetcher = fetch
): Promise<Response | null> {
  const url = new URL(request.url);
  if (url.pathname !== LatestLocalAgentReleasePath) {
    return null;
  }
  if (request.method !== "GET") {
    return new Response("Method Not Allowed", { status: 405, headers: { Allow: "GET" } });
  }

  try {
    const manifestResponse = await fetchManifest(ManifestUrl, {
      headers: { Accept: "application/json" }
    });
    if (!manifestResponse.ok) {
      throw new Error(`Release manifest returned ${manifestResponse.status}.`);
    }

    const release = parseLatestLocalAgentRelease(await manifestResponse.json());
    return Response.json(release, {
      headers: { "Cache-Control": "no-store" }
    });
  } catch {
    return Response.json(
      { error: "The latest Salesko Local Agent release is unavailable." },
      { status: 502, headers: { "Cache-Control": "no-store" } }
    );
  }
}

export async function fetchLatestLocalAgentRelease(
  fetchRelease: Fetcher = fetch
): Promise<LatestLocalAgentRelease> {
  const response = await fetchRelease(LatestLocalAgentReleasePath, {
    headers: { Accept: "application/json" }
  });
  if (!response.ok) {
    throw new Error(`Could not load the latest Salesko Local Agent release (${response.status}).`);
  }

  return parseLatestLocalAgentRelease(await response.json());
}
